import { useEffect, useState } from "react";
import { Wallet, BarChart3, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, rp, todayJkt } from "@/lib/api";
import { EmptyState, Loading, PageHeader, SectionTitle, SimBadge, StatCard } from "@/components/common";
import { describe } from "@/components/History";

export const monthRange = (m) => {
  const [y, mo] = m.split("-").map(Number);
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return [`${m}-01`, `${m}-${String(last).padStart(2, "0")}`];
};

export default function UangKas() {
  const { ws, version } = useAuth();
  const mats = Object.fromEntries(ws.materials.map((m) => [m.key, m]));
  const [month, setMonth] = useState(todayJkt().slice(0, 7));
  const [dash, setDash] = useState(null);
  const [r, setR] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/dashboard").then(({ data }) => setDash(data)).catch((e) => toast.error(errMsg(e))); }, [version]);
  useEffect(() => {
    if (!month) return;
    const [start, end] = monthRange(month);
    setR(null);
    api.get("/report", { params: { start, end } }).then(({ data }) => setR(data)).catch((e) => toast.error(errMsg(e)));
  }, [month, version]);
  const moves = (r?.cash_movements || []).filter((t) => { const d = describe(t, mats); return `${d.title} ${d.sub} ${t.note}`.toLowerCase().includes(q.toLowerCase()); });
  return (
    <div data-testid="uang-kas-page">
      <PageHeader icon={Wallet} title="Uang Kas" subtitle="Saldo uang usaha Anda, dihitung otomatis dari semua catatan.">
        <input type="month" className="jt-input w-44" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="cash-month-input" />
      </PageHeader>
      {!dash ? <Loading /> : (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <StatCard big icon={Wallet} label="Uang Kas" value={rp(dash.cash)} hint="Saldo uang berdasarkan catatan transaksi." testid="cash-balance" />
          <StatCard big icon={BarChart3} color="#b7861b" label="Uang Kas Setelah Kasbon" value={rp(dash.after_credit)} hint="Simulasi jika semua kasbon pelanggan diterima dan semua kasbon pabrik dilunasi." testid="cash-after-credit" className="bg-[#fffaf0]" />
        </div>
      )}
      <div className="grid xl:grid-cols-[0.8fr_1.2fr] gap-6">
        <div className="jt-card p-5 sm:p-6 self-start">
          <SectionTitle>Ringkasan Bulan Ini</SectionTitle>
          {!r || !dash ? <Loading /> : (
            <dl className="divide-y text-sm sm:text-[15px]">
              {[["Total Pemasukan", rp(r.total_masuk), "text-[#1f7a46]", "cash-total-in"], ["Total Pengeluaran", rp(r.total_keluar), "text-[#c0392b]", "cash-total-out"],
                ["Uang Kas Awal Bulan", rp(r.opening.cash), "", "cash-opening"], ["Uang Kas Akhir Bulan", rp(r.closing.cash), "", "cash-closing"],
                ["Kasbon Pelanggan (Sisa)", rp(dash.cust_credit), "text-[#6d4fd8]", "cash-cust-credit"], ["Kasbon Pabrik (Sisa)", rp(dash.sup_credit), "text-[#2357c9]", "cash-sup-credit"]].map(([k, v, cls, id]) => (
                <div key={k} className="flex justify-between py-2.5"><dt className="text-muted-foreground">{k}</dt><dd className={`font-extrabold ${cls}`} data-testid={id}>{v}</dd></div>
              ))}
            </dl>
          )}
        </div>
        <div className="jt-card p-5 sm:p-6">
          <SectionTitle>Riwayat Perubahan Uang Kas</SectionTitle>
          <div className="relative mb-2"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input className="jt-input pl-9" placeholder="Cari transaksi..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="cash-search-input" /></div>
          {!r ? <Loading /> : moves.length === 0 ? <EmptyState text={q ? "Tidak ada hasil yang cocok." : "Belum ada perubahan Uang Kas di bulan ini."} testid="cash-history-empty" /> : (
            <div className="divide-y divide-[var(--jt-gold)]/15">
              {moves.map((t) => { const d = describe(t, mats); return (
                <div key={t.id} className="flex items-center gap-3 py-3" data-testid="cash-move-row">
                  <div className="w-14 text-xs text-muted-foreground shrink-0">{fmtDate(t.date, { day: "2-digit", month: "short" })}</div>
                  <div className="flex-1 min-w-0"><div className="flex flex-wrap gap-1.5 items-center"><span className="font-bold text-sm">{d.title}</span>{t.is_simulation && <SimBadge />}</div><div className="text-xs text-muted-foreground truncate">{t.note || d.sub}</div></div>
                  <div className="text-right"><div className={`font-extrabold text-sm ${t.effect > 0 ? "text-[#1f7a46]" : "text-[#c0392b]"}`}>{t.effect > 0 ? "+" : "-"}{rp(Math.abs(t.effect))}</div><div className="text-xs text-muted-foreground">Saldo {rp(t.saldo)}</div></div>
                </div>); })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
